import {
  Box,
  Button,
  Flex,
  Grid,
  SegmentedControl,
  Text,
} from '@radix-ui/themes';
import { SessionRecapHelper } from 'classes/helpers/session-recap.helper';
import { CommonSelectInput } from 'components/common/form/select';
import { SearchHitter } from 'components/common/hitters/search-hitter';
import {
  PlotHoverProvider,
  usePlotHover,
} from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plot-hover';
import 'components/sections/analytics/session-hits/recap/index.scss';
import { SessionRecapPitchCard } from 'components/sections/analytics/session-hits/recap/pitch-card';
import { RecapFieldView } from 'components/sections/analytics/session-hits/recap/visualizations/field-view';
import { RecapColorLegend } from 'components/sections/analytics/session-hits/recap/visualizations/legend';
import { RecapStrikeZoneView } from 'components/sections/analytics/session-hits/recap/visualizations/strike-zone-view';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import {
  DEMO_RECAP_COUNT,
  generateDemoSessionHits,
} from 'components/sections/feature-demo/charts/session-recap-fixtures';
import { MachineContext } from 'contexts/machine.context';
import {
  RecapColorBy,
  RecapFieldMode,
  RecapZoneOverlay,
} from 'enums/session-recap/enums';
import { t } from 'i18next';
import { PlateHelper } from 'lib_ts/classes/plate.helper';
import { HitterSide } from 'lib_ts/enums/hitters.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HittersService } from 'services/hitters.service';
import useHittersStore from 'stores/use-hitters-store';

const toggleLegendKey = (key: string) => {
  const current = useAnalyticsStore.getState().legendKeys ?? [];

  const next = current.includes(key)
    ? current.filter((k) => k !== key) // toggle off
    : [...current, key]; // toggle on

  useAnalyticsStore.setState({ legendKeys: next.length ? next : null });
};

/** annotated videos from recent sessions on this machine */
const loadDemoVideoIDs = async (machineID: string): Promise<string[]> => {
  const summaries = await HittersService.getInstance().getSessionSummaries({
    limit: 8,
    machineID,
  });
  const seen = new Set<string>();

  for (const summary of summaries) {
    if (seen.size >= 16) {
      break;
    }

    const hits = await HittersService.getInstance().getSessionHits(summary._id);

    hits.forEach((hit) => {
      const videoID = hit.fire?.annotated_video_id;

      if (videoID) {
        seen.add(videoID);
      }
    });
  }

  return [...seen];
};

/**
 * Field spray and zones, and the three strike-zone modes, stay mounted
 * together so they can be checked without toggling. A dot writes
 * selectedHitID, which opens the pitch card.
 */
export const FeatureDemoSessionRecap = () => {
  const { machine } = useContext(MachineContext);

  // keep the seed and hit count constant so the demo charts look the same on every reload
  const [seed, setSeed] = useState(1337);
  const [hitCount, setHitCount] = useState(DEMO_RECAP_COUNT);

  const [videoIDs, setVideoIDs] = useState<string[]>([]);

  useEffect(() => {
    const machineID = machine.machineID;

    if (!machineID) {
      return;
    }

    let cancelled = false;

    loadDemoVideoIDs(machineID).then((ids) => {
      if (!cancelled) {
        setVideoIDs(ids);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [machine.machineID]);

  return (
    <PlotHoverProvider>
      <FeatureDemoSessionRecapCharts
        seed={seed}
        hitCount={hitCount}
        videoIDs={videoIDs}
        onHitCountChange={setHitCount}
        onRegenerate={() => {
          useAnalyticsStore.setState({ selectedHitID: null });
          setSeed(Date.now());
        }}
      />
    </PlotHoverProvider>
  );
};

const FeatureDemoSessionRecapCharts = (props: {
  seed: number;
  hitCount: number;
  videoIDs: string[];
  onHitCountChange: (count: number) => void;
  onRegenerate: () => void;
}) => {
  const activeHitter = useHittersStore((s) => s.activeHitter);
  const filtered = useHittersStore((s) => s.filtered);

  const hitterSide = HitterSide.RHH;

  const { hoveredItemKey } = usePlotHover();
  const hoveredHitID = hoveredItemKey ?? null;

  const colorByID = useAnalyticsStore((s) => s.colorByID);
  const legendKeys = useAnalyticsStore((s) => s.legendKeys);
  const selectedHitID = useAnalyticsStore((s) => s.selectedHitID);
  const zoneFilter = useAnalyticsStore((s) => s.zoneFilter);

  const hitCountRef = useRef(props.hitCount);
  hitCountRef.current = props.hitCount;
  const [hoveredLegendKey, setHoveredLegendKey] = useState<string | null>(null);
  const onLegendEnter = useCallback((key: string) => {
    setHoveredLegendKey(key);
  }, []);
  const onLegendLeave = useCallback((key: string) => {
    setHoveredLegendKey((current) => (current === key ? null : current));
  }, []);

  const hits = useMemo(
    () => generateDemoSessionHits(props.seed, props.videoIDs, props.hitCount),
    [props.seed, props.videoIDs, props.hitCount]
  );
  const absSzDef = useMemo(
    () => PlateHelper.getMlbAbsStrikeZone(activeHitter?.height_ft),
    [activeHitter?.height_ft]
  );

  const records = useMemo(
    () => SessionRecapHelper.convertToRecap(hits, absSzDef),
    [hits, absSzDef]
  );
  const byID = useMemo(
    () =>
      records.reduce(
        (acc, r) => {
          acc[r.hit._id] = r;
          return acc;
        },
        {} as Record<string, (typeof records)[number]>
      ),
    [records]
  );
  const { shots, cardHits } = useMemo(() => {
    if (!legendKeys && !zoneFilter) {
      return { shots: records, cardHits: hits };
    }

    const selected = legendKeys ? new Set(legendKeys) : null;

    const isMatch = (r: (typeof records)[number]) => {
      if (selected) {
        const key = SessionRecapHelper.getLegendKey(r, colorByID);

        if (!key || !selected.has(key)) {
          return false;
        }
      }

      return (
        !zoneFilter ||
        SessionRecapHelper.isInChartZone(r, zoneFilter, hitterSide, absSzDef)
      );
    };

    // The open pitch and the dot under the cursor stay plotted, but the card
    // arrows only step through pitches that match the filters.
    return {
      shots: records.filter(
        (r) =>
          isMatch(r) ||
          r.hit._id === hoveredHitID ||
          r.hit._id === selectedHitID
      ),
      cardHits: records.filter(isMatch).map((r) => r.hit),
    };
  }, [
    hits,
    records,
    legendKeys,
    zoneFilter,
    colorByID,
    hoveredHitID,
    selectedHitID,
    hitterSide,
    absSzDef,
  ]);

  // keep track of the previous (real) selectedHitID so it can be restored when the (demo) component unmounts
  useEffect(() => {
    const backup = useAnalyticsStore.getState().selectedHitID;
    useAnalyticsStore.setState({ selectedHitID: null });

    return () => {
      useAnalyticsStore.setState({ selectedHitID: backup });
    };
  }, []);

  return (
    <>
      <Box className="SessionRecap">
        <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
          <Flex gap="2" align="center">
            <CommonSelectInput
              id="demo-recap-hit-count"
              options={[
                { label: '10 pitches', value: '10' },
                { label: '50 pitches', value: '50' },
                { label: '100 pitches', value: '100' },
                { label: '500 pitches', value: '500' },
                { label: '1000 pitches', value: '1000' },
                { label: '5000 pitches', value: '5000' },
              ]}
              value={props.hitCount.toString()}
              onNumericChange={(value) => {
                props.onHitCountChange(value);
              }}
              skipSort
            />

            <Button
              variant="soft"
              color={RADIX.COLOR.NEUTRAL}
              onClick={props.onRegenerate}
            >
              Generate
            </Button>

            <Box flexGrow="1">{/* placeholder */}</Box>

            <SearchHitter hitters={filtered} />
          </Flex>

          <Flex justify="between" align="center" gap="3" wrap="wrap">
            <Flex gap={RADIX.FLEX.GAP.SM} align="center">
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                {t('hitters.recap-color-by')}
              </Text>
              <SegmentedControl.Root
                size="1"
                value={colorByID}
                onValueChange={(v) =>
                  useAnalyticsStore.setState({
                    colorByID: v as RecapColorBy,
                    legendKeys: null,
                  })
                }
              >
                {Object.values(RecapColorBy).map((v) => (
                  <SegmentedControl.Item key={v} value={v}>
                    {t(SessionRecapHelper.getLabelForColorBy(v))}
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl.Root>
            </Flex>
          </Flex>

          <Grid columns={{ initial: '1', md: '2' }} gap={RADIX.FLEX.GAP.SM}>
            <RecapFieldView
              records={records}
              shots={shots}
              hitterSide={hitterSide}
              mode={RecapFieldMode.Spray}
              instanceID="demo-spray"
              highlightKey={hoveredLegendKey}
            />
            <RecapFieldView
              records={records}
              shots={shots}
              hitterSide={hitterSide}
              mode={RecapFieldMode.Zones}
              instanceID="demo-zones"
              highlightKey={hoveredLegendKey}
            />
          </Grid>

          <Grid columns={{ initial: '1', md: '3' }} gap={RADIX.FLEX.GAP.SM}>
            <RecapStrikeZoneView
              records={records}
              shots={shots}
              szDef={absSzDef}
              overlayID={RecapZoneOverlay.None}
              highlightKey={hoveredLegendKey}
            />
            <RecapStrikeZoneView
              records={records}
              shots={shots}
              szDef={absSzDef}
              overlayID={RecapZoneOverlay.Zones}
              showShots={false}
              highlightKey={hoveredLegendKey}
            />
            <RecapStrikeZoneView
              records={records}
              shots={shots}
              szDef={absSzDef}
              overlayID={RecapZoneOverlay.Heatmap}
              showShots={false}
              highlightKey={hoveredLegendKey}
            />
          </Grid>

          <RecapColorLegend
            colorBy={colorByID}
            records={records}
            activeKeys={legendKeys}
            onToggle={toggleLegendKey}
            onClear={() => useAnalyticsStore.setState({ legendKeys: null })}
            onHoverStart={onLegendEnter}
            onHoverEnd={onLegendLeave}
          />
        </Flex>
      </Box>

      <SessionRecapPitchCard
        sortedHits={cardHits}
        byID={byID}
        szDef={absSzDef}
      />
    </>
  );
};
